import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { clearAuthSession, getMyCurrentPlan } from '../services/authApi';
import { 
  Search, PlayCircle, Clock, 
  BookOpen, ChevronRight, BarChart, Sparkles 
} from 'lucide-react';

export default function Library() {
  const navigate = useNavigate();
  const categories = ["All Modules", "Finance & Accounting", "Income Generation", "Investing & Crypto"];
  const [activeCategory, setActiveCategory] = useState(categories[0]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);

  useEffect(() => {
    const checkAccess = async () => {
      const token = localStorage.getItem('authToken');

      if (!token) {
        navigate('/pricing', { replace: true });
        return;
      }

      if (localStorage.getItem('paymentSubmitted') === 'true') {
        setIsCheckingAccess(false);
        return;
      }

      try {
        const plan = await getMyCurrentPlan();
        const hasPaidAccess = plan?.status === 'active';

        if (!hasPaidAccess) {
          navigate('/pricing', { replace: true });
          return;
        }

        setIsCheckingAccess(false);
      } catch (error) {
        if (error.message === 'Unauthorized') {
          clearAuthSession();
        }
        navigate('/pricing', { replace: true });
      }
    };

    checkAccess();
  }, [navigate]);

  // Enriched data
  const courses = [
    {
      id: "FIN-101",
      category: "Finance & Accounting",
      title: "Accounting Made Easy: Master Budgets & Tax",
      desc: "Build a solid financial foundation for your personal life or business. Track cash flow and stay on top of your taxes.",
      image: "https://images.unsplash.com/photo-1556740758-90de374c12ad?auto=format&fit=crop&w=800&q=80",
      duration: "4h 15m",
      lessons: 12,
      level: "Beginner",
      color: "blue"
    },
    {
      id: "INC-201",
      category: "Income Generation",
      title: "Remote Income Strategies & Passive Wealth",
      desc: "Unlock over 20 verified remote income streams and automate your revenue directly from your laptop.",
      image: "https://images.unsplash.com/photo-1517048676732-d65bc937f952?auto=format&fit=crop&w=800&q=80",
      duration: "3h 45m",
      lessons: 8,
      level: "Intermediate",
      color: "pink"
    },
    {
      id: "MKT-301",
      category: "Income Generation",
      title: "Affiliate Marketing Mastery & Funnel Building",
      desc: "Build high-converting sales funnels and earn recurring commissions by promoting products globally.",
      image: "https://images.unsplash.com/photo-1533750516457-a7f992034fec?auto=format&fit=crop&w=800&q=80",
      duration: "6h 20m",
      lessons: 18,
      level: "Advanced",
      color: "blue"
    },
    {
      id: "CRY-401",
      category: "Investing & Crypto",
      title: "Cryptocurrency Investing: Web3 & Tokenomics",
      desc: "Understand blockchain technology, evaluate tokenomics, and safely grow your digital portfolio with confident trades.",
      image: "https://images.unsplash.com/photo-1621416894569-0f39ed31d247?auto=format&fit=crop&w=800&q=80",
      duration: "5h 30m",
      lessons: 15,
      level: "Intermediate",
      color: "pink"
    }
  ];

  const visibleCourses = courses.filter(course => {
    const matchesCategory = activeCategory === "All Modules" || course.category === activeCategory;
    const matchesSearch = course.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          course.desc.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  if (isCheckingAccess) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0F172A] text-white">
        <div className="text-center">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-2 border-slate-700 border-t-[#2563EB]" />
          <p className="text-sm font-semibold text-slate-400">Checking library access...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen font-sans text-white bg-[#0F172A] relative">

      {/* SUBTLE BACKGROUND GLOWS */}
      <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.03] mix-blend-overlay" />
        <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] bg-[#2563EB]/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[600px] h-[600px] bg-[#F43F5E]/10 rounded-full blur-[150px]" />
      </div>

      <div className="relative z-50">
        <Header />
      </div>

      <main className="w-full flex-grow relative z-10 pt-28 pb-24 max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* DIRECTORY HEADER & SEARCH */}
        <div className="mb-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-8">
            <div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-white mb-4">
                Module <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2563EB] to-[#F43F5E]">Library</span>
              </h1>
              <p className="text-slate-400 text-lg">
                Browse our comprehensive database of financial courses, frameworks, and investment strategies.
              </p>
            </div>
          </div>

          {/* Master Search Bar */}
          <div className="relative w-full group">
            <div className="absolute inset-0 bg-gradient-to-r from-[#2563EB] to-[#F43F5E] rounded-2xl blur opacity-25 group-focus-within:opacity-50 transition-opacity duration-300" />
            <div className="relative flex items-center bg-[#1E293B] border border-slate-700/80 rounded-2xl p-2 shadow-xl">
              <Search className="text-slate-400 w-6 h-6 ml-4 mr-3" />
              <input 
                type="text" 
                placeholder="What do you want to master today? (e.g. 'Crypto', 'Budgeting')" 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-transparent border-none py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-0 text-lg"
              />
            </div>
          </div>
        </div>

        {/* CATEGORY FILTER PILLS */}
        <div className="flex items-center gap-3 overflow-x-auto no-scrollbar pb-6 mb-8 border-b border-slate-800">
          {categories.map((cat, idx) => {
            const isActive = activeCategory === cat;
            return (
              <button
                key={idx}
                onClick={() => setActiveCategory(cat)}
                className={`whitespace-nowrap px-6 py-2.5 rounded-full text-sm font-bold transition-all duration-300 border
                  ${isActive
                    ? 'bg-white text-[#0F172A] border-white shadow-[0_0_15px_rgba(255,255,255,0.2)]'
                    : 'bg-[#1E293B]/50 border-slate-700/80 text-slate-400 hover:bg-[#1E293B] hover:text-white hover:border-slate-600'
                  }
                `}
              >
                {cat}
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <h2 className="text-xl font-bold text-slate-300 flex flex-wrap items-center gap-2">
            Showing Results <ChevronRight className="w-4 h-4 text-slate-600" /> 
            <span className="text-white">{activeCategory}</span>
          </h2>
          <span className="text-sm font-medium text-slate-500">
            {visibleCourses.length} {visibleCourses.length === 1 ? 'Module' : 'Modules'} found
          </span>
        </div>

        {/* ========================================= */}
        {/* UNBOXED GRID WITH LINE SEPARATORS         */}
        {/* ========================================= */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-slate-800">
          {visibleCourses.map((course, idx) => {
            const isBlue = course.color === 'blue';
            const accentColor = isBlue ? 'text-[#2563EB]' : 'text-[#F43F5E]';

            return (
              <div 
                key={course.id} 
                /* This class logic handles the vertical lines perfectly:
                  - Mobile: bottom border only
                  - Tablet (2 cols): right border on odd items, bottom border on all
                  - Desktop (3 cols): right border on 1st & 2nd items, none on 3rd, bottom border on all
                */
                className="cursor-pointer group flex flex-col py-6 lg:p-6 sm:p-8 border-b border-slate-800 md:border-r md:[&:nth-child(2n)]:border-r-0 lg:[&:nth-child(2n)]:border-r lg:[&:nth-child(3n)]:border-r-0 hover:bg-slate-800/20 transition-colors duration-300"
              >
                {/* Image Container */}
                <div className="relative w-full aspect-[16/10] overflow-hidden rounded-2xl bg-slate-900 border border-slate-700/50 mb-6">
                  <img 
                    src={course.image} 
                    alt={course.title}
                    className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700 ease-out"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] to-transparent opacity-60" />
                  
                  {/* Hover Play Overlay */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 bg-black/20 backdrop-blur-sm">
                    <div className="bg-white/10 backdrop-blur-md border border-white/30 p-4 rounded-full text-white transform scale-75 group-hover:scale-100 transition-transform duration-300">
                      <PlayCircle size={32} className="ml-1 fill-white/20" strokeWidth={1.5} />
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="flex flex-col flex-grow">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className={`w-4 h-4 ${accentColor}`} />
                    <span className={`text-[11px] font-black tracking-widest uppercase ${accentColor}`}>
                      {course.category}
                    </span>
                  </div>

                  <h3 className="text-xl md:text-2xl font-bold text-white mb-3 leading-snug group-hover:text-slate-200 transition-colors">
                    {course.title}
                  </h3>
                  
                  <p className="text-sm text-slate-400 leading-relaxed mb-8">
                    {course.desc}
                  </p>

                  {/* Meta Data Row - Clean, no background boxes */}
                  <div className="mt-auto pt-6 border-t border-slate-800/50 flex items-center flex-wrap gap-4 text-xs font-semibold text-slate-400">
                    <div className="flex items-center gap-1.5">
                      <Clock size={14} className={accentColor} /> {course.duration}
                    </div>
                    <div className="w-1 h-1 rounded-full bg-slate-700" />
                    <div className="flex items-center gap-1.5">
                      <BookOpen size={14} className={accentColor} /> {course.lessons} Mods
                    </div>
                    <div className="w-1 h-1 rounded-full bg-slate-700" />
                    <div className="flex items-center gap-1.5">
                      <BarChart size={14} className={accentColor} /> {course.level}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty State */}
        {visibleCourses.length === 0 && (
          <div className="w-full py-20 flex flex-col items-center justify-center text-center border-t border-slate-800">
            <Search className="w-12 h-12 text-slate-600 mb-4" />
            <h3 className="text-2xl font-bold text-white mb-2">No modules found</h3>
            <p className="text-slate-400">We couldn't find any courses matching "{searchQuery}".</p>
            <button 
              onClick={() => {setSearchQuery(''); setActiveCategory('All Modules')}}
              className="mt-6 px-6 py-2.5 bg-slate-800 hover:bg-slate-700 text-white rounded-xl font-bold transition-colors"
            >
              Reset
            </button>
          </div>
        )}

      </main>

      <div className="relative z-50">
        <Footer />
      </div>

      <style dangerouslySetInnerHTML={{__html: `
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}} />
    </div>
  );
}
